import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { WhiteLabelService } from '@flexyoo/common';
import type { EmployeeSelectedBenefit } from '@flexyoo/employee/models';
import { employeeSelectedBenefitsSelectors } from '@flexyoo/employee/store';
import {
  excludeVisibleCurrentCart,
  getEmployeeCartArchiveStatusColor,
  groupEmployeeCartsByStatus,
  isEmployeeCartVisible,
  shouldGroupEmployeeArchiveCarts,
} from '@flexyoo/employee/util';
import { LoaderComponent } from '@flexyoo/shared/ui';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { EmployeeBasketCartArchiveItemComponent } from '../employee-basket-cart-archive-item/employee-basket-cart-archive-item.component';

@Component({
  selector: 'flexyoo-employee-basket-cart-archive',
  templateUrl: './employee-basket-cart-archive.component.html',
  styleUrl: './employee-basket-cart-archive.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UpperCasePipe,
    MatExpansionModule,
    TranslateModule,
    LoaderComponent,
    EmployeeBasketCartArchiveItemComponent,
  ],
})
export class EmployeeBasketCartArchiveComponent {
  private readonly store = inject(Store);
  private readonly wls = inject(WhiteLabelService);

  readonly signCart = output<number>();
  readonly signBenefit = output<EmployeeSelectedBenefit>();

  private readonly currentCart = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCart)
  );

  // the archive is part of the portfolio the store already loads, so it never asks for it again
  private readonly cartHistories = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCartHistories),
    { initialValue: null }
  );

  protected readonly isLoading = computed(() => this.cartHistories() === null);

  protected readonly carts = computed(() => {
    const currentCart = this.currentCart();

    return excludeVisibleCurrentCart(
      this.cartHistories() ?? [],
      currentCart?.id,
      currentCart?.state
    );
  });

  protected readonly shouldGroup = computed(() => {
    const currentCart = this.currentCart();
    const hasVisibleCurrentCart = !!currentCart?.id && isEmployeeCartVisible(currentCart.state);
    return shouldGroupEmployeeArchiveCarts(this.carts().length, hasVisibleCurrentCart);
  });

  protected readonly statusGroups = computed(() => {
    const colors = {
      primaryColor: this.wls.primaryColor,
      warnColor: this.wls.warnColor,
    };

    return groupEmployeeCartsByStatus(this.carts()).map((group) => ({
      ...group,
      statusColor: getEmployeeCartArchiveStatusColor(group.kind, colors),
    }));
  });

  protected onSignCart(cartId: number): void {
    this.signCart.emit(cartId);
  }

  protected onSignBenefit(benefit: EmployeeSelectedBenefit): void {
    this.signBenefit.emit(benefit);
  }
}
